import { useAppStore } from '@runtime/core/store/appStore'
import { fetchJSON } from '@runtime/shared/api/fetchJSON'
import type { UserServerPayload } from '@runtime/core/store/appStoreTypes'
import type { GameStatsSnapshot } from './gameResults'

const RETRY_DELAY_MS = 15_000
const BATCH_SIZE = 50

export function startUserSessionSync(authToken: string) {
  let stopped = false
  let running = false
  let retry: ReturnType<typeof setTimeout> | undefined
  const isCurrent = () =>
    !stopped && useAppStore.getState().authToken === authToken
  const headers = { Authorization: `Bearer ${authToken}` }

  async function sync() {
    if (!isCurrent() || running) return
    running = true
    clearTimeout(retry)
    try {
      const profile = await fetchJSON<UserServerPayload>('/api/me', { headers })
      if (!isCurrent()) return
      useAppStore.getState().setUserFromServer(profile)
      const userId = String(profile.id ?? '')
      if (!userId) return
      while (isCurrent()) {
        const pending = (
          useAppStore.getState().gameResultsOutbox[userId] ?? []
        ).slice(0, BATCH_SIZE)
        if (!pending.length) break
        const snapshot = await fetchJSON<GameStatsSnapshot>(
          '/api/me/game-results',
          {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              results: pending.map(
                ({ statsRecorded: _statsRecorded, ...result }) => result,
              ),
            }),
          },
        )
        if (!isCurrent()) return
        useAppStore.getState().acknowledgeGameResults(
          userId,
          pending.map((result) => result.id),
          snapshot,
        )
      }
    } catch (error) {
      if (!isCurrent()) return
      const status =
        error && typeof error === 'object' && 'status' in error
          ? Number(error.status)
          : null
      if (status === 401 || status === 403) useAppStore.getState().logout()
      else
        retry = setTimeout(() => {
          void sync()
        }, RETRY_DELAY_MS)
    } finally {
      running = false
    }
  }

  const unsubscribe = useAppStore.subscribe((state, previous) => {
    if (state.gameResultsOutbox !== previous.gameResultsOutbox) void sync()
  })
  const resume = () => {
    void sync()
  }
  window.addEventListener('online', resume)
  window.addEventListener('focus', resume)
  void sync()
  return () => {
    stopped = true
    clearTimeout(retry)
    unsubscribe()
    window.removeEventListener('online', resume)
    window.removeEventListener('focus', resume)
  }
}
